import { lazy, useState, Suspense } from "react";
import { Add, Building, TickCircle, Clock, WalletMinus } from "iconsax-react";
import cn from "classnames";
import { Button, Badge } from "@/shared/ui";
import { formatDateForDisplay, formatMoneyKzt } from "@/shared/utils";
import { getStatusBadgeColor } from "@/features/self/shared";
import { getActStatusLabel } from "../../utils";
import type { MsbCompletionAct } from "../../types/msb";
import { useMsbCalculationsPage } from "../../hooks/useMsbCalculationsPage";
import ContractProgressCircle from "../CalculationsPage/components/ContractProgressCircle";
import MsbCalculationsPageSkeleton from "./MsbCalculationsPageSkeleton";
import MsbHistoryBlock from "./MsbHistoryBlock";

const MsbCreateActModal = lazy(() => import("./MsbCreateActModal"));

export default function MSBCalculationsPage() {
  const {
    contract,
    isLoading,
    isNotFound,
    summary,
    sortedActs,
    createModalOpen,
    setCreateModalOpen,
    t,
    locale,
  } = useMsbCalculationsPage();

  const [editAct, setEditAct] = useState<MsbCompletionAct | null>(null);

  if (isLoading && !contract) {
    return (
      <>
        <title>{t("meta.calculationsTitle")}</title>
        <section className="flex-1">
          <div className="flex items-center justify-between pb-4 pt-1 border-b surface-base-stroke mb-5">
            <h1 className="text-display-xs content-base-primary">{t("calculations.title")}</h1>
          </div>
          <MsbCalculationsPageSkeleton />
        </section>
      </>
    );
  }

  return (
    <>
      <title>{t("meta.calculationsTitle")}</title>
      <section className="flex-1">
        <div className="flex items-center justify-between pb-4 pt-1 border-b surface-base-stroke mb-5">
          <h1 className="text-display-xs content-base-primary">{t("calculations.title")}</h1>
          {contract && (
            <Button variant="primary" size="lg" onClick={() => setCreateModalOpen(true)}>
              <Add size={16} color="currentColor" />
              {t("calculations.generateAvr")}
            </Button>
          )}
        </div>

        {isNotFound || !contract ? (
          <div className="flex flex-col gap-5 p-5 radius-lg border surface-component-stroke">
            <p className="text-body-regular-md content-action-neutral">{t("calculations.noContract")}</p>
          </div>
        ) : (
          <>
            {/* Contract Dashboard */}
            <div className="p-5 radius-3xl surface-base-fill mb-5">
              <div className="flex items-start justify-between gap-4 mb-5">
                <div className="flex items-center gap-3">
                  <div className="w-10 aspect-square radius-xs text-white radial-glow background-neutral-fill flex items-center justify-center">
                    <Building size={24} color="currentColor" />
                  </div>
                  <div className="flex flex-col gap-1">
                    <h3 className="text-body-bold-lg content-base-primary">
                      {contract.commercial_org.name_ru}
                    </h3>
                    <p className="text-body-regular-sm content-action-neutral">
                      БИН: {contract.commercial_org.bin}
                    </p>
                  </div>
                </div>

                <div className="flex flex-col items-end gap-2 shrink-0">
                  <Badge
                    variant="soft"
                    color={getStatusBadgeColor(contract.status)}
                    text={getActStatusLabel(contract.status, t)}
                    icon={<TickCircle size={12} color="currentColor" variant="Bold" />}
                  />
                  <span className="text-body-regular-sm content-action-neutral">
                    {formatDateForDisplay(contract.service_start_date)} —{" "}
                    {formatDateForDisplay(contract.service_end_date)}
                  </span>
                </div>
              </div>

              <div className="grid grid-cols-[auto_1fr] gap-10 items-center">
                <div>
                  <ContractProgressCircle
                    paidPercent={summary.paidPercent}
                    approvedPercent={summary.approvedPercent}
                    contractAmount={summary.contractAmount}
                    locale={locale}
                  />
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[
                    {
                      label: t("calculations.dashboard.alreadyPaid"),
                      value: formatMoneyKzt(summary.totalPaid, locale),
                      icon: TickCircle,
                      iconColor: "text-white radial-glow background-positive-fill",
                    },
                    {
                      label: t("calculations.dashboard.awaitingPayment"),
                      value: formatMoneyKzt(summary.totalApproved, locale),
                      icon: Clock,
                      iconColor: "text-white radial-glow background-notice-fill",
                    },
                    {
                      label: t("calculations.dashboard.contractRemainder"),
                      value: formatMoneyKzt(summary.remaining, locale),
                      icon: WalletMinus,
                      iconColor: "text-white radial-glow background-info-fill",
                    },
                  ].map((card) => {
                    const Icon = card.icon;
                    return (
                      <div key={card.label} className="p-5 radius-lg border surface-component-stroke flex flex-col">
                        <div className="flex items-center gap-3 mb-3">
                          <div
                            className={cn(
                              "w-8 aspect-square flex justify-center items-center surface-component-fill radius-xs",
                              card.iconColor,
                            )}>
                            <Icon size={16} color="currentColor" variant="Bold" />
                          </div>
                          <p className="text-label-sm content-base-primary">{card.label}</p>
                        </div>
                        <h6 className="text-display-sm content-base-primary">{card.value}</h6>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

            <MsbHistoryBlock acts={sortedActs} locale={locale} onEdit={(act) => setEditAct(act)} />
          </>
        )}
      </section>

      {createModalOpen && contract && (
        <Suspense fallback={null}>
          <MsbCreateActModal
            contract={contract}
            onClose={() => setCreateModalOpen(false)}
            onSuccess={() => setCreateModalOpen(false)}
          />
        </Suspense>
      )}

      {editAct && contract && (
        <Suspense fallback={null}>
          <MsbCreateActModal
            contract={contract}
            onClose={() => setEditAct(null)}
            onSuccess={() => setEditAct(null)}
            editAct={editAct}
          />
        </Suspense>
      )}
    </>
  );
}
